import { Component, OnInit, OnDestroy, ElementRef, ViewChild, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthorizePaymentService } from 'src/app/shared/services/api/authorize-payment.service';
import { getEditTokenPage, getEditTokenPageResponse, NotifyTokenEditRequest, NotifyTokenEditResponse } from 'src/app/shared/models/authorizeNet.model';
import { environment } from 'src/environments/environment';
import { ApiEndpoints } from 'src/config/api-endpoints';

declare global {
 interface Window {
  AuthorizeNetIFrame?: any;
 }
}

export interface HostedPaymentEditData {
 userCreditCardID: string;
}

@Component({
 selector: 'app-hosted-payment-edit-popup',
 templateUrl: './hosted-payment-edit-popup.component.html',
 styleUrls: ['./hosted-payment-edit-popup.component.scss']
})

export class HostedPaymentEditPopupComponent implements OnInit, OnDestroy {

 loading: boolean = true;
 systemError: boolean = false;
 
 @ViewChild('editPaymentIframe') editPaymentIframe: ElementRef | undefined;
 
 private subscription: Subscription = new Subscription();
 private iframeWasSubmitted: boolean = false; 
 paymentSuccessful: boolean = false;
 
 private isModalOpen: boolean = true;
 
 private communicatorUrl: string = `${environment.walletAPPUrl}/assets/web/hostedPaymentIFrameCommunicatorUrl.html`;

 private userId = sessionStorage.getItem('PPIUserID') || ''; // Ensure non-null initialization
 private userCreditCardID: string; // Passed via dialog data

 constructor(
  public dialogRef: MatDialogRef<HostedPaymentEditPopupComponent>,
  private router: Router,
  private authorizePaymentService: AuthorizePaymentService,
  private sanitizer: DomSanitizer,
  // Inject the data passed from the parent component
  @Inject(MAT_DIALOG_DATA) public data: HostedPaymentEditData
 ) {
  // Assign card ID from injected data
  this.userCreditCardID = data.userCreditCardID;

  // Router listener to handle external redirect (broken communication fallback)
  this.subscription.add(this.router.events.pipe(
   filter(event => event instanceof NavigationEnd)
  ).subscribe((event: NavigationEnd) => {
   // If the page navigates away to home, treat it as success detection (fallback)
   if (this.iframeWasSubmitted && (event.url === '/home' || event.url === '/')) {
    this.displaySuccessAndNotifyServer(true); // true = success
   }
  }));
 }

 ngOnInit() {
  this.loadHostedPaymentEditForm();
 }

 ngOnDestroy() {
  this.subscription.unsubscribe();
 }
 
 // Closes modal and triggers success logic if form was submitted
 closeModal(result: 'success' | 'cancel'): void {
  this.isModalOpen = false;
  
  // CRITICAL LOGIC: If user closes manually AND we submitted, assume success 
  // (since the actual external communication channel is unreliable)
  if (result === 'cancel' && this.iframeWasSubmitted && !this.paymentSuccessful) {
    console.warn('Manual closure detected after form submission. Triggering immediate server notify.');
    // We display the success message immediately, which then triggers the server notify
    this.displaySuccessAndNotifyServer(true); 
    return; // Execution will continue in displaySuccessAndNotifyServer's subscribe/setTimeout
  }

  // If not a manual trigger (i.e., true cancellation or successful notify already ran), just close cleanly
  // Hide the iframe element explicitly
  const ifrm = window.document.getElementById('edit_payment') as HTMLIFrameElement;
  if (ifrm) {
    ifrm.style.visibility = 'hidden';
    ifrm.style.display = 'none';
  }

  this.dialogRef.close(result);
 }

 private displaySuccessAndNotifyServer(triggerServerNotify: boolean): void {
  this.iframeWasSubmitted = false; 

  // 1. Hide the Iframe container immediately
  const ifrm = window.document.getElementById('edit_payment') as HTMLIFrameElement;
  if (ifrm) {
    ifrm.style.display = 'none';
    ifrm.style.visibility = 'hidden';
  }

  this.paymentSuccessful = true; // Show success overlay 
  
  if (triggerServerNotify) {
    // Prepare Notify Edit Request (WalletID fixed to prevent double quoting/slashes)
    const rawWalletID = sessionStorage.getItem('WalletID') || '';
    const cleanWalletID = rawWalletID.replace(/\\?\"/g, ''); 
    
    const notifyRequest: NotifyTokenEditRequest = {
      walletID: cleanWalletID, 
      sessionID: sessionStorage.getItem('SessionID') || '',
      userID: this.userId,
      userCreditCardID: this.userCreditCardID, // Pass the card ID back
    };
    
    this.authorizePaymentService.notifyTokenEdit(notifyRequest).subscribe({
      next: (response: NotifyTokenEditResponse) => {
        if (response.success) {
          console.log('DWNotifyTokenEdit API call successful (Manual).');
          // Close modal and return 'success' result
          setTimeout(() => this.dialogRef.close('success'), 1500); 
        } else {
          console.error('DWNotifyTokenEdit API failed after successful submission.');
          // Close modal and return 'cancel' result for failure
          setTimeout(() => this.dialogRef.close('cancel'), 1500); 
        }
      },
      error: (err) => {
        console.error('Error calling DWNotifyTokenEdit API:', err);
        // Close modal and return 'cancel' result for error
        setTimeout(() => this.dialogRef.close('cancel'), 1500);
      }
    });
  } else {
    // If server notify wasn't triggered, close cleanly.
    this.dialogRef.close('cancel');
  }
 }

 private loadHostedPaymentEditForm() {
  this.loading = true;
  const sessionId = sessionStorage.getItem('SessionID') || '';
    // ✅ FIX: Use JSON.parse correctly
  const rawWalletID = sessionStorage.getItem('WalletID'); 
  const walletID = rawWalletID ? JSON.parse(rawWalletID) : null; 

  const getEditTokenPageRequest: getEditTokenPage = { 
    userCreditCardID: this.userCreditCardID, 
    walletID: walletID || '', 
    sessionID: sessionId, 
    userid: this.userId
  };

  this.authorizePaymentService.getEditTokenPage(getEditTokenPageRequest).subscribe({
   next: (response: getEditTokenPageResponse) => {
    this.loading = false;
    if (response.success && response.data?.editForm) {
     const formToken = response.data.editForm.match(/value='([^']*)'/)?.[1];
     const paymentProfileId = response.data.editForm.match(/name="paymentProfileId" value='([^']*)'/)?.[1];
     
     if (formToken && paymentProfileId) {
      this.loadIframeWithEditForm(formToken, paymentProfileId);
      this.isModalOpen = true; 
     } else {
      this.systemError = true;
     }
    } else {
     this.systemError = true;
    }
   },
   error: (err) => {
    this.loading = false;
    this.systemError = true;
   }
  });
 }
 
 private loadIframeWithEditForm(formToken: string, paymentProfileId: string) {
  const form = document.createElement('form');
  form.setAttribute('id', 'send_token_edit');
  form.setAttribute('method', 'post');
  form.setAttribute('action', ApiEndpoints.authorizeneteditpayment); // Edit URL
  form.setAttribute('target', 'authorizeNetIframe'); 

  const communicatorUrl = this.communicatorUrl;

  // Add required hidden inputs
  form.innerHTML = `
   <input type="hidden" name="token" value="${formToken}" />
   <input type="hidden" name="paymentProfileId" value="${paymentProfileId}" />
   <input type="hidden" name="hostedPaymentIFrameCommunicatorUrl" value="${communicatorUrl}" />
  `;

  document.body.appendChild(form);
  
  this.iframeWasSubmitted = true;
  
  // Assuming you have a ViewChild reference in the HTML using #editPaymentIframe
  const iframeElement = this.editPaymentIframe?.nativeElement; 
  if (iframeElement) {
   iframeElement.style.visibility = 'visible';
   iframeElement.style.display = 'block';
  }

  form.submit();
  document.body.removeChild(form);
 }
}
